// src/pages/Mafia/mafiaTableToPdf.js
//
// The "Print MAFIA Table" PDF (landscape letter, jsPDF + jspdf-autotable).
// Moved out of Print.js so that the same code can be run by the PDF test.
//
// 2026-09-29, asked for by the engineers:
//   - heading: "MAFIA Table" / "S.S. White Part Number: …" / "Part Revision: 01";
//     no company name, no address (mafiaPrintHeading.js);
//   - the first three columns are narrow, "Additional Info" gets all the room
//     that is left (122 pt instead of about 68);
//   - the text of "Additional Info" stays inside its cell (mafiaPdfText.js).

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { cleanPdfText, toPlainPdfText, builtInFontCanDraw, useSymbolFont } from "./mafiaPdfText";
import { MAFIA_TABLE_TITLE, partNumberLabel, partRevisionText, revisionLine } from "./mafiaPrintHeading";

const M = 36;                 // page margin (pt)
const INK = 33;
const MUTED = 110;

const HEAD = [[
  "MOM OP #", "DNA", "Dimension Type", "Unit", "Nominal", "Min", "Max",
  "Tolerance Class", "In Process", "Method Of Recording",
  "Special Instructions", "WI or ATP #", "Gages", "Additional Info",
]];

const ADDITIONAL_INFO = 13;

// Letter landscape is 792 pt wide: 720 pt between the margins. The thirteen
// fixed columns take 598 pt, "Additional Info" the 122 pt that are left.
const COLUMN_STYLES = {
  0: { cellWidth: 32, halign: "center" },  // MOM OP #
  1: { cellWidth: 34 },                    // DNA
  2: { cellWidth: 50 },                    // Dimension Type
  3: { cellWidth: 32, halign: "center" },  // Unit
  4: { cellWidth: 42, halign: "right" },   // Nominal
  5: { cellWidth: 38, halign: "right" },   // Min
  6: { cellWidth: 38, halign: "right" },   // Max
  7: { cellWidth: 52 },                    // Tolerance Class
  8: { cellWidth: 38 },                    // In Process
  9: { cellWidth: 74 },                    // Method Of Recording
  10: { cellWidth: 66 },                   // Special Instructions
  11: { cellWidth: 46 },                   // WI or ATP #
  12: { cellWidth: 56 },                   // Gages
  13: { cellWidth: "auto" },               // Additional Info
};

/**
 * @param {object}   p
 * @param {string}   p.partNum
 * @param {number}   p.rev              revision of the drawing
 * @param {number}   p.revMinor         MAFIA revision
 * @param {string}   p.createdBy        "First Last (id)"
 * @param {string}   p.branding         sswhite | shukla | sss
 * @param {object[]} p.rows             the rows of the preview
 * @param {string}  [p.generated]       date and time text
 * @param {string}  [p.symbolFont]      DejaVu Sans Condensed, base64 (none = built-in font only)
 * @returns the jsPDF document
 */
export function buildMafiaTableDoc({ partNum, rev, revMinor, createdBy, branding, rows, generated, symbolFont }) {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "letter" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  const symbols = useSymbolFont(doc, symbolFont);
  // Every column but "Additional Info" is printed with the built-in font.
  const plain = (v) => toPlainPdfText(v);
  // "Additional Info": the symbol font for the whole column, so that all its
  // rows look the same; without the font the text is brought down to the
  // built-in one.
  const info = (v) => (symbols ? symbols.fit(v) : toPlainPdfText(v));

  const part = cleanPdfText(partNum) || "—";

  // ── Heading ──────────────────────────────────────────────
  doc.setFont("helvetica", "bolditalic").setFontSize(18).setTextColor(INK);
  doc.text(MAFIA_TABLE_TITLE, M, 30);
  doc.setFont("helvetica", "bold").setFontSize(14).setTextColor(INK);
  doc.text(plain(`${partNumberLabel(branding)}: ${part}`), M, 50);

  doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(MUTED);
  doc.text(`${revisionLine(rev, revMinor)}    Rows: ${(rows || []).length}`, M, 66);
  doc.text(
    plain(`Created By: ${createdBy || "—"}    Generated: ${generated || new Date().toLocaleString("en-US")}`),
    M, 79
  );

  doc.setDrawColor(INK).setLineWidth(0.8);
  doc.line(M, 88, pageW - M, 88);

  // ── Table ────────────────────────────────────────────────
  const body = (rows || []).map((r) => [
    plain(r.MomOpNumber), plain(r.DNA), plain(r.DimensionType), plain(r.Unit),
    plain(r.Nominal), plain(r.Min), plain(r.Max), plain(r.ToleranceClass), plain(r.InProcess),
    plain(r.MethodOfRecording), plain(r.SpecialInstructions), plain(r.WIATPNumber),
    plain(r.Gages), info(r.AdditionalInfo),
  ]);

  const footer = `${part} · Part Rev ${partRevisionText(rev)} · MAFIA Rev ${revMinor}`;

  autoTable(doc, {
    startY: 96,
    margin: { left: M, right: M },
    head: HEAD,
    body,
    theme: "grid",
    rowPageBreak: "avoid",     // a line of the table is not cut in two by a page break
    styles: {
      font: "helvetica",
      fontSize: 7.5, cellPadding: 3, textColor: INK,
      lineColor: [200, 200, 200], lineWidth: 0.4, valign: "top",
      overflow: "linebreak",   // wrap long cells instead of clipping
    },
    headStyles: {
      fillColor: [54, 54, 70], textColor: 255, fontStyle: "bold",
      fontSize: 7.5, halign: "left", font: "helvetica",
    },
    alternateRowStyles: { fillColor: [247, 247, 250] },
    columnStyles: COLUMN_STYLES,
    // The symbol font has no bold: it is set for the body cells only.
    didParseCell: (data) => {
      if (symbols && data.section === "body" && data.column.index === ADDITIONAL_INFO) {
        data.cell.styles.font = symbols.font;
        data.cell.styles.fontStyle = "normal";
      }
    },
  });

  // Page numbers when the number of pages is known (written while the table
  // was drawn, the first page said "of 1" on a table of two pages).
  const pageCount = doc.internal.getNumberOfPages();
  for (let page = 1; page <= pageCount; page++) {
    doc.setPage(page);
    doc.setFont("helvetica", "normal").setFontSize(7.5).setTextColor(MUTED);
    doc.text(`${plain(footer)} · Page ${page} of ${pageCount}`, pageW - M, pageH - 16, { align: "right" });
  }

  return doc;
}

/** File name of the download: MAFIA_<part>_PartRev_01_MafiaRev_3.pdf */
export function mafiaTableFileName(partNum, rev, revMinor) {
  const part = String(partNum || "part").replace(/[^A-Za-z0-9._-]/g, "_");
  return `MAFIA_${part}_PartRev_${partRevisionText(rev)}_MafiaRev_${revMinor}.pdf`;
}

export { builtInFontCanDraw };
